import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Container, Form, Table, Card, Row, Col, Modal } from 'react-bootstrap';
import { hireRateFieldGroupClass } from '../constants/hireRateFields';

const NAME_MAX_LENGTH = 60;

const RATE_COLUMNS = [
  { field: 'week_rate', label: 'Week Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'day_rate', label: 'Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'month_rate', label: 'Month Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'one_day_rate', label: '1 Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'two_day_rate', label: '2 Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'three_day_rate', label: '3 Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'four_day_rate', label: '4 Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'part_day_rate', label: 'Part Day Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'weekend_rate', label: 'W/E Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'hour_rate', label: 'Hour Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'sc_rate', label: 'SC Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'unit_rate', label: 'Unit Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'switch_week_rate', label: 'Switch Week Rate', type: 'money', min: 0.01, step: '0.01' },
  { field: 'switch_after_weeks', label: 'Switch After Weeks', type: 'integer', min: 1, step: '1' },
  { field: 'deposit', label: 'Deposit', type: 'money', min: 0.01, step: '0.01' },
];

const GRID_COLUMNS = [
  { field: 'week_rate', label: 'Weekly Rate' },
  { field: 'day_rate', label: 'Daily Rate' },
  { field: 'sc_rate', label: 'SC' },
];

function emptyFormData() {
  return {
    name: '',
    ...Object.fromEntries(RATE_COLUMNS.map(({ field }) => [field, ''])),
  };
}

function toFormData(hireRate) {
  if (!hireRate) return emptyFormData();
  const data = { name: hireRate.name ?? '' };
  for (const col of RATE_COLUMNS) {
    const val = hireRate[col.field];
    data[col.field] = val === null || val === undefined ? '' : String(val);
  }
  return data;
}

function parsePayload(formData) {
  const payload = { name: formData.name?.trim() || '' };
  for (const col of RATE_COLUMNS) {
    const raw = formData[col.field];
    if (raw === null || raw === undefined || raw === '') {
      payload[col.field] = null;
      continue;
    }
    payload[col.field] = col.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
  }
  return payload;
}

function formatRate(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return Number.isNaN(n) ? '—' : n.toFixed(2);
}

function HireRateModal({ hireRate, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyFormData());
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    setFormData(toFormData(hireRate));
    setModalError('');
  }, [hireRate]);

  const handleChange = (field, value) => {
    if (field === 'name' && value.length > NAME_MAX_LENGTH) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (modalError) setModalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setModalError('Name is required');
      return;
    }
    try {
      await onSave(parsePayload(formData));
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save hire rate');
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-pound-sign me-2 text-primary"></i>
          {hireRate ? 'Edit Hire Rate' : 'Add Hire Rate'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              maxLength={NAME_MAX_LENGTH}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>
          <Row>
            {RATE_COLUMNS.map((col) => (
              <Col md={6} lg={4} key={col.field}>
                <Form.Group className={`mb-3 ${hireRateFieldGroupClass(col.field)}`}>
                  <Form.Label>{col.label}</Form.Label>
                  <Form.Control
                    type="number"
                    step={col.step}
                    min={col.min}
                    value={formData[col.field]}
                    onChange={(e) => handleChange(col.field, e.target.value)}
                  />
                </Form.Group>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : hireRate ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function HireRates() {
  const [hireRates, setHireRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHireRate, setEditingHireRate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchHireRates = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-rates', {
        params: { page: 1, limit: 10000, search },
      });
      setHireRates(response.data.hireRates);
    } catch (err) {
      setError('Failed to fetch hire rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHireRates();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHireRates(searchTerm);
  };

  const handleAddHireRate = () => {
    setEditingHireRate(null);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleEditHireRate = (hireRate) => {
    setEditingHireRate(hireRate);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSaveHireRate = async (payload) => {
    setSaving(true);
    try {
      if (editingHireRate) {
        const response = await axios.put(`/api/hire-rates/${editingHireRate.id}`, payload);
        let successMsg = 'Hire rate updated successfully';
        if (response.data?.rateCascadeUpdates) {
          const { hireStockIndividual, hireStockBulk } = response.data.rateCascadeUpdates;
          const total = (hireStockIndividual || 0) + (hireStockBulk || 0);
          if (total > 0) {
            const parts = [];
            if (hireStockIndividual > 0) parts.push(`${hireStockIndividual} hire stock`);
            if (hireStockBulk > 0) parts.push(`${hireStockBulk} bulk hire stock`);
            successMsg += `. Rates applied to ${parts.join(' and ')} item(s).`;
          }
        }
        setSuccess(successMsg);
      } else {
        await axios.post('/api/hire-rates', payload);
        setSuccess('Hire rate added successfully');
      }
      setShowModal(false);
      setEditingHireRate(null);
      fetchHireRates(searchTerm);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hire rate?')) return;
    setError('');
    try {
      await axios.delete(`/api/hire-rates/${id}`);
      setSuccess('Hire rate deleted successfully');
      fetchHireRates(searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete hire rate');
    }
  };

  const handleDeleteAll = async () => {
    if (hireRates.length === 0) return;
    if (!window.confirm('Are you sure you want to delete ALL hire rates?')) return;
    if (
      !window.confirm(
        'This will permanently delete all your hire rate records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    setError('');
    try {
      await axios.delete('/api/hire-rates');
      setSuccess('All hire rates deleted successfully');
      fetchHireRates(searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all hire rates');
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-pound-sign me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Hire Rates</h1>
              <p className="text-muted mb-0">Manage hire rate profiles and pricing tiers</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddHireRate} className="me-2">
            Add Hire Rate
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={hireRates.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <div className="card-header">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Search hire rates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '250px' }}
            />
            <Button type="submit" variant="primary" size="sm">
              <i className="bi bi-search me-1"></i> Search
            </Button>
          </form>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading hire rates...
            </div>
          ) : hireRates.length === 0 ? (
            <div className="text-center p-4 text-muted">
              <i className="bi bi-currency-pound" style={{ fontSize: '2rem' }}></i>
              <p className="mb-0 mt-2">
                No hire rates found. Click &quot;Add Hire Rate&quot; to get started.
              </p>
            </div>
          ) : (
            <div className="table-responsive scrollable-grid">
              <Table className="table-hover mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    {GRID_COLUMNS.map((col) => (
                      <th key={col.field}>{col.label}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hireRates.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      {GRID_COLUMNS.map((col) => (
                        <td key={col.field}>{formatRate(row[col.field])}</td>
                      ))}
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditHireRate(row)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteRow(row.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <HireRateModal
          hireRate={editingHireRate}
          saving={saving}
          onClose={() => {
            if (!saving) {
              setShowModal(false);
              setEditingHireRate(null);
            }
          }}
          onSave={handleSaveHireRate}
        />
      )}
    </Container>
  );
}

export default HireRates;
